import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../App';
import BottomNav from '../components/BottomNav';
import SearchBar from '../components/SearchBar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Bookmark, BookmarkCheck, StickyNote, Share2, FileDown, Image, Highlighter } from 'lucide-react';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';

export default function ProvisionDetailPage() {
  const { provisionId } = useParams();
  const navigate = useNavigate();
  const [provision, setProvision] = useState(null);
  const [section, setSection] = useState(null);
  const [reference, setReference] = useState(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedText, setSelectedText] = useState('');
  const [selectionRange, setSelectionRange] = useState(null);
  const [highlights, setHighlights] = useState([]);

  useEffect(() => {
    fetchData();
    checkBookmark();
    fetchHighlights();
  }, [provisionId]);

  const fetchData = async () => {
    try {
      const provisionResponse = await api.get(`/provisions/${provisionId}`);
      setProvision(provisionResponse.data);

      const [sectionResponse, refResponse] = await Promise.all([
        api.get(`/sections/${provisionResponse.data.section_id}`),
        api.get(`/references/${provisionResponse.data.reference_id}`),
      ]);

      setSection(sectionResponse.data);
      setReference(refResponse.data);
    } catch (error) {
      toast.error('Failed to load provision');
    } finally {
      setLoading(false);
    }
  };

  const checkBookmark = async () => {
    try {
      const response = await api.get('/bookmarks');
      const bookmarked = response.data.some(
        (item) => item.bookmark.provision_id === provisionId
      );
      setIsBookmarked(bookmarked);
    } catch (error) {
      console.warn('Bookmark check skipped:', error.message);
    }
  };

  const fetchHighlights = async () => {
    try {
      const response = await api.get(`/provisions/${provisionId}/highlights`);
      setHighlights(response.data || []);
    } catch (error) {
      // User might not be authenticated or no highlights exist
      console.log('No highlights found or user not authenticated');
    }
  };

  const handleTextSelection = () => {
    const selection = window.getSelection();
    const text = selection.toString().trim();
    
    if (text && provision) {
      // Calculate the start and end offsets in the provision content
      const range = selection.getRangeAt(0);
      const preSelectionRange = range.cloneRange();
      const contentElement = document.querySelector('.provision-content');
      
      if (contentElement && contentElement.contains(range.commonAncestorContainer)) {
        preSelectionRange.selectNodeContents(contentElement);
        preSelectionRange.setEnd(range.startContainer, range.startOffset);
        
        const start = preSelectionRange.toString().length;
        const end = start + text.length;
        
        setSelectedText(text);
        setSelectionRange({ start, end });
        
        // Scroll the highlight box into view on mobile
        setTimeout(() => {
          const highlightBox = document.getElementById('highlight-action-box');
          if (highlightBox) {
            highlightBox.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 100);
      }
    } else if (!text) {
      // Clear selection if no text is selected
      setSelectedText('');
      setSelectionRange(null);
    }
  };

  const saveHighlight = async () => {
    if (!selectedText || !selectionRange) {
      toast.error('Please select text to highlight');
      return;
    }

    try {
      const response = await api.post('/highlights', {
        provision_id: provisionId,
        section_id: provision.section_id,
        reference_id: provision.reference_id,
        highlighted_text: selectedText,
        start_offset: selectionRange.start,
        end_offset: selectionRange.end,
        color: 'yellow',
      });
      
      toast.success('Highlight saved!');
      setSelectedText('');
      setSelectionRange(null);
      window.getSelection().removeAllRanges();
      
      // Refresh highlights to show the new one
      await fetchHighlights();
    } catch (error) {
      console.error('Failed to save highlight:', error);
      toast.error(error.response?.data?.detail || 'Failed to save highlight');
    }
  };

  const renderHighlightedContent = () => {
    if (!provision || !provision.content) return '';
    
    if (highlights.length === 0) {
      return provision.content;
    }

    // Sort highlights by start position
    const sortedHighlights = [...highlights].sort((a, b) => a.start_offset - b.start_offset);
    
    const colorMap = {
      yellow: 'bg-yellow-200 dark:bg-yellow-800',
      blue: 'bg-blue-200 dark:bg-blue-800',
      green: 'bg-green-200 dark:bg-green-800',
      pink: 'bg-pink-200 dark:bg-pink-800',
    };
    
    let result = [];
    let lastIndex = 0;
    
    sortedHighlights.forEach((highlight, idx) => {
      const { start_offset, end_offset, color, id } = highlight;
      
      // Add text before highlight
      if (start_offset > lastIndex) {
        result.push(
          <span key={`text-${idx}`}>{provision.content.substring(lastIndex, start_offset)}</span>
        );
      }
      
      // Add highlighted text
      const highlightColor = colorMap[color] || colorMap.yellow;
      result.push(
        <mark 
          key={`highlight-${id}`}
          className={`${highlightColor} px-1 rounded cursor-pointer transition-all hover:opacity-80`}
          style={{ 
            display: 'inline',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
            maxWidth: '100%',
            whiteSpace: 'pre-wrap'
          }}
          title="Click to remove highlight"
          onClick={(e) => {
            e.stopPropagation();
            removeHighlight(id);
          }}
        >
          {provision.content.substring(start_offset, end_offset)}
        </mark>
      );
      
      lastIndex = end_offset;
    });
    
    // Add remaining text
    if (lastIndex < provision.content.length) {
      result.push(
        <span key="text-end">{provision.content.substring(lastIndex)}</span>
      );
    }
    
    return result;
  };

  const removeHighlight = async (highlightId) => {
    try {
      await api.delete(`/highlights/${highlightId}`);
      toast.success('Highlight removed');
      await fetchHighlights();
    } catch (error) {
      toast.error('Failed to remove highlight');
    }
  };

  const toggleBookmark = async () => {
    try {
      if (isBookmarked) {
        await api.delete(`/bookmarks/${provisionId}`);
        setIsBookmarked(false);
        toast.success('Bookmark removed');
      } else {
        await api.post('/bookmarks', {
          provision_id: provisionId,
          section_id: provision.section_id,
          reference_id: provision.reference_id,
        });
        setIsBookmarked(true);
        toast.success('Bookmarked!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update bookmark');
    }
  };

  const saveNote = async () => {
    try {
      await api.post('/notes', {
        provision_id: provisionId,
        section_id: provision.section_id,
        reference_id: provision.reference_id,
        content: noteContent,
      });
      toast.success('Note saved!');
      setNoteDialogOpen(false);
      setNoteContent('');
    } catch (error) {
      toast.error('Failed to save note');
    }
  };

  const shareAsText = () => {
    const text = `${section.number}: ${section.title}\n\n${provision.content}\n\nSource: ${reference.short_name} - LexProcure PH App\nGet the app: ${window.location.origin}`;
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const exportAsPDF = () => {
    const printWindow = window.open('', '_blank');
    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${section.number} - ${section.title}</title>
          <script src="https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"></script>
          <style>
            body {
              font-family: 'Times New Roman', serif;
              max-width: 800px;
              margin: 40px auto;
              padding: 20px;
              line-height: 1.6;
              color: #333;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 3px solid #10b981;
              padding-bottom: 20px;
            }
            .logo {
              font-size: 24px;
              font-weight: bold;
              color: #10b981;
              margin-bottom: 10px;
            }
            .reference {
              font-size: 18px;
              color: #059669;
              font-weight: bold;
              margin-bottom: 10px;
            }
            .section-title {
              font-size: 20px;
              font-weight: bold;
              margin: 20px 0;
              color: #1e293b;
            }
            .content {
              text-align: justify;
              margin: 20px 0;
              white-space: pre-wrap;
            }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 2px solid #e2e8f0;
              font-size: 12px;
              color: #64748b;
              text-align: center;
            }
            .download-btn {
              position: fixed;
              top: 20px;
              right: 20px;
              background: #10b981;
              color: white;
              border: none;
              padding: 12px 24px;
              border-radius: 8px;
              cursor: pointer;
              font-size: 16px;
              font-weight: bold;
              box-shadow: 0 2px 8px rgba(0,0,0,0.2);
            }
            .download-btn:hover {
              background: #059669;
            }
          </style>
        </head>
        <body>
          <button class="download-btn" onclick="downloadPDF()">Download PDF</button>
          <div class="header">
            <div class="logo">LexProcure PH</div>
            <div class="reference">${reference.short_name}</div>
            <div class="section-title">${section.number}: ${section.title}</div>
          </div>
          <div class="content">${provision.content}</div>
          <div class="footer">
            <p>Philippine Procurement Law Digital Library</p>
            <p>LexProcure PH | ${window.location.origin}</p>
            <p>For informational purposes only. Not legal advice.</p>
          </div>
          <script>
            function downloadPDF() {
              const { jsPDF } = window.jspdf;
              const doc = new jsPDF();
              const pageWidth = doc.internal.pageSize.getWidth();
              const pageHeight = doc.internal.pageSize.getHeight();
              const margin = 20;
              const maxWidth = pageWidth - (margin * 2);
              let yPosition = margin;

              // Header - Logo
              doc.setFontSize(20);
              doc.setTextColor(16, 185, 129);
              doc.setFont('helvetica', 'bold');
              doc.text('LexProcure PH', pageWidth / 2, yPosition, { align: 'center' });
              yPosition += 10;

              // Reference name
              doc.setFontSize(14);
              doc.setTextColor(5, 150, 105);
              doc.text('${reference.short_name}', pageWidth / 2, yPosition, { align: 'center' });
              yPosition += 8;

              // Section title
              doc.setFontSize(12);
              doc.setTextColor(30, 41, 59);
              doc.setFont('helvetica', 'bold');
              const titleText = '${section.number}: ${section.title}';
              const titleLines = doc.splitTextToSize(titleText, maxWidth);
              doc.text(titleLines, pageWidth / 2, yPosition, { align: 'center' });
              yPosition += (titleLines.length * 6) + 5;

              // Separator line
              doc.setDrawColor(16, 185, 129);
              doc.setLineWidth(0.5);
              doc.line(margin, yPosition, pageWidth - margin, yPosition);
              yPosition += 15;

              // Content
              doc.setFontSize(11);
              doc.setTextColor(51, 51, 51);
              doc.setFont('times', 'normal');
              
              const contentText = ${JSON.stringify(provision.content)};
              const contentLines = doc.splitTextToSize(contentText, maxWidth);
              
              for (let i = 0; i < contentLines.length; i++) {
                if (yPosition > pageHeight - 40) {
                  doc.addPage();
                  yPosition = margin;
                }
                doc.text(contentLines[i], margin, yPosition);
                yPosition += 6;
              }

              // Footer on last page
              const lastPageHeight = doc.internal.pageSize.getHeight();
              yPosition = lastPageHeight - 25;
              doc.setDrawColor(226, 232, 240);
              doc.setLineWidth(0.3);
              doc.line(margin, yPosition - 5, pageWidth - margin, yPosition - 5);
              
              doc.setFontSize(9);
              doc.setTextColor(100, 116, 139);
              doc.setFont('helvetica', 'normal');
              doc.text('Philippine Procurement Law Digital Library', pageWidth / 2, yPosition, { align: 'center' });
              doc.text('LexProcure PH | ${window.location.origin}', pageWidth / 2, yPosition + 5, { align: 'center' });
              doc.text('For informational purposes only. Not legal advice.', pageWidth / 2, yPosition + 10, { align: 'center' });

              // Download
              const filename = '${section.number.replace(/\s+/g, '_')}_${section.title.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30)}.pdf';
              doc.save(filename);
            }
          </script>
        </body>
      </html>
    `;
    printWindow.document.open();
    printWindow.document.write(content);
    printWindow.document.close();
    toast.success('PDF preview opened. Click "Download PDF" to save to your Downloads folder.');
  };

  const exportAsImage = () => {
    try {
      // Calculate proper height based on content
      const canvas = document.createElement('canvas');
      canvas.width = 1400; // Wider canvas for better readability
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('Unable to get canvas context');
      }

      // Temporary height calculation
      canvas.height = 2000; // Temporary for measurement
      
      // Calculate content height with proper wrapping
      ctx.font = '30px Georgia, serif'; // Better font for readability
      const padding = 150; // Much more padding for breathing room
      const maxWidth = canvas.width - (padding * 2);
      const lineHeight = 65; // Much more space between lines for better readability
      
      // Split content by line breaks to preserve structure (like whitespace-pre-wrap does)
      const contentLines = provision.content.split('\n');
      let lines = [];
      
      // Process each line from the content
      for (let lineIndex = 0; lineIndex < contentLines.length; lineIndex++) {
        const originalLine = contentLines[lineIndex].trim();
        
        // Skip empty lines but mark them for paragraph breaks
        if (originalLine === '') {
          if (lines.length > 0 && !lines[lines.length - 1].isEmptyLine) {
            lines.push({ text: '', isEmptyLine: true, isParagraphBreak: true });
          }
          continue;
        }
        
        // Check if this is a section header (ends with : or starts with caps and short)
        const isHeader = originalLine.endsWith(':') && originalLine.length < 100;
        
        if (isHeader) {
          // Add header as-is
          lines.push({ 
            text: originalLine, 
            isHeader: true, 
            isEmptyLine: false,
            isParagraphBreak: lineIndex > 0 && contentLines[lineIndex - 1].trim() === ''
          });
        } else {
          // Wrap long lines into multiple lines for display
          const words = originalLine.split(' ');
          let currentLine = '';
          let isFirstLineOfParagraph = true;
          
          for (let i = 0; i < words.length; i++) {
            const testLine = currentLine + words[i] + ' ';
            const metrics = ctx.measureText(testLine);
            
            if (metrics.width > maxWidth * 0.90 && i > 0) {
              lines.push({ 
                text: currentLine.trim(), 
                isHeader: false, 
                isEmptyLine: false,
                isParagraphBreak: isFirstLineOfParagraph && lineIndex > 0 && contentLines[lineIndex - 1].trim() === ''
              });
              currentLine = words[i] + ' ';
              isFirstLineOfParagraph = false;
            } else {
              currentLine = testLine;
            }
          }
          if (currentLine.trim()) {
            lines.push({ 
              text: currentLine.trim(), 
              isHeader: false, 
              isEmptyLine: false,
              isParagraphBreak: isFirstLineOfParagraph && lineIndex > 0 && contentLines[lineIndex - 1].trim() === ''
            });
          }
        }
      }

      // Calculate actual height needed (account for paragraph breaks and headers)
      let estimatedHeight = 0;
      
      lines.forEach(line => {
        if (line.isEmptyLine) return; // Skip empty lines in calculation
        
        if (line.isHeader) {
          estimatedHeight += 60; // Header spacing
        } else {
          estimatedHeight += 50; // Line spacing
        }
        
        if (line.isParagraphBreak) {
          estimatedHeight += 50; // Paragraph break spacing
        }
      });
      
      const totalHeight = Math.max(1200, 450 + estimatedHeight + 300);
      
      // Set actual canvas height
      canvas.height = totalHeight;

      // Background gradient (softer colors)
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, '#ffffff');
      gradient.addColorStop(1, '#f0fdf4');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Top accent bar
      ctx.fillStyle = '#10b981';
      ctx.fillRect(0, 0, canvas.width, 12);

      let currentY = 100;

      // Title - centered
      ctx.font = 'bold 52px sans-serif';
      ctx.fillStyle = '#10b981';
      ctx.textAlign = 'center';
      ctx.fillText('LexProcure PH', canvas.width / 2, currentY);

      currentY += 70;

      // Reference - centered
      ctx.font = '34px sans-serif';
      ctx.fillStyle = '#059669';
      ctx.fillText(reference.short_name, canvas.width / 2, currentY);

      currentY += 80;

      // Section title - left aligned with proper padding
      ctx.font = 'bold 38px sans-serif';
      ctx.fillStyle = '#1e293b';
      ctx.textAlign = 'left';
      const sectionTitle = `${section.number}: ${section.title}`;
      
      // Wrap section title if too long
      const sectionWords = sectionTitle.split(' ');
      let sectionLine = '';
      for (let word of sectionWords) {
        const testLine = sectionLine + word + ' ';
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth) {
          ctx.fillText(sectionLine.trim(), padding, currentY);
          currentY += 48;
          sectionLine = word + ' ';
        } else {
          sectionLine = testLine;
        }
      }
      if (sectionLine.trim()) {
        ctx.fillText(sectionLine.trim(), padding, currentY);
      }

      currentY += 60;

      // Divider line
      ctx.strokeStyle = '#cbd5e1';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(padding, currentY);
      ctx.lineTo(canvas.width - padding, currentY);
      ctx.stroke();

      currentY += 60;

      // Content text - properly formatted with headers and paragraph breaks
      ctx.textAlign = 'left';

      // Render each line with proper spacing, headers, and paragraph breaks
      for (let i = 0; i < lines.length; i++) {
        const lineObj = lines[i];
        
        // Skip empty lines (they're just for spacing)
        if (lineObj.isEmptyLine) {
          continue;
        }
        
        // Add paragraph break spacing before this line if needed
        if (lineObj.isParagraphBreak) {
          currentY += 50; // Extra space between paragraphs (matches page)
        }
        
        // Style differently for headers vs regular text
        if (lineObj.isHeader) {
          ctx.font = 'bold 34px sans-serif';
          ctx.fillStyle = '#059669'; // Green color for headers
          ctx.fillText(lineObj.text, padding, currentY);
          currentY += 60; // Space after header
        } else {
          ctx.font = '30px Georgia, serif';
          ctx.fillStyle = '#1e293b';
          ctx.fillText(lineObj.text, padding, currentY);
          currentY += 50; // Space between lines (generous)
        }
      }

      currentY += 60;

      // Bottom divider
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(padding, currentY);
      ctx.lineTo(canvas.width - padding, currentY);
      ctx.stroke();

      currentY += 50;

      // Footer - centered and smaller
      ctx.font = '20px sans-serif';
      ctx.fillStyle = '#64748b';
      ctx.textAlign = 'center';
      ctx.fillText('Shared from LexProcure PH', canvas.width / 2, currentY);
      currentY += 35;
      ctx.font = '18px sans-serif';
      ctx.fillStyle = '#94a3b8';
      ctx.fillText(window.location.origin, canvas.width / 2, currentY);

      // Convert to blob and download
      canvas.toBlob((blob) => {
        if (!blob) {
          toast.error('Failed to generate image. Please try again.');
          return;
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const filename = `${reference.short_name.replace(/\s+/g, '-')}-${section.number.replace(/\s+/g, '-')}.png`;
        link.download = filename;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Also open in new tab so user can see it
        const previewWindow = window.open(url, '_blank');
        if (previewWindow) {
          setTimeout(() => {
            URL.revokeObjectURL(url);
          }, 3000);
          toast.success(`Image downloaded as "${filename}"! Check your Downloads folder or the new tab that opened.`, {
            duration: 6000
          });
        } else {
          setTimeout(() => {
            URL.revokeObjectURL(url);
          }, 100);
          toast.success(`Image downloaded as "${filename}" to your Downloads folder! (Pop-up blocked - check browser settings)`, {
            duration: 6000
          });
        }
      }, 'image/png');
      
    } catch (error) {
      console.error('Export image error:', error);
      toast.error('Failed to export image. Please try again or contact support.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-emerald-50 dark:from-slate-900 dark:via-slate-800 dark:to-emerald-900 pb-24">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-slate-200 dark:bg-slate-700 rounded w-1/3"></div>
            <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!provision) {
    return null;
  }

  return (
    <>
      <style>{`
        .provision-content mark {
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
          word-break: normal !important;
          white-space: pre-wrap !important;
          display: inline !important;
          max-width: 100% !important;
          box-decoration-break: clone !important;
          -webkit-box-decoration-break: clone !important;
          hyphens: none !important;
          -webkit-hyphens: none !important;
        }
        .provision-content {
          max-width: 100% !important;
          overflow-wrap: break-word !important;
          word-break: normal !important;
          overflow-x: hidden !important;
          hyphens: none !important;
          -webkit-hyphens: none !important;
        }
        @media (max-width: 640px) {
          .provision-content {
            word-break: normal !important;
            overflow-wrap: break-word !important;
            hyphens: none !important;
          }
          .provision-content mark {
            word-break: normal !important;
            overflow-wrap: break-word !important;
            hyphens: none !important;
          }
        }
      `}</style>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-emerald-50 dark:from-slate-900 dark:via-slate-800 dark:to-emerald-900 pb-24 overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 py-8 overflow-x-hidden">
          <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        {reference && section && (
          <div className="text-sm text-slate-600 dark:text-slate-400 mb-6">
            <span className="font-medium text-emerald-600 dark:text-emerald-400">{reference.short_name}</span>
            <span className="mx-2">/</span>
            <span>{section.number}</span>
          </div>
        )}

        {section && (
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-800 dark:text-slate-100 mb-8" style={{ fontFamily: 'IBM Plex Sans' }}>
            {section.title}
          </h1>
        )}

        {selectedText && (
          <div 
            id="highlight-action-box"
            className="mb-4 p-4 bg-green-50 dark:bg-green-900 border-2 border-green-500 dark:border-green-600 rounded-lg shadow-lg animate-in slide-in-from-top-2 duration-200"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-1">Text selected</p>
                <p className="text-sm text-green-900 dark:text-green-100 italic line-clamp-2">"{selectedText}"</p>
              </div>
              <Button
                onClick={() => {
                  setSelectedText('');
                  setSelectionRange(null);
                  window.getSelection().removeAllRanges();
                }}
                variant="ghost"
                size="sm"
                className="shrink-0 h-8 w-8 p-0 text-green-700 hover:text-green-900 dark:text-green-300"
              >
                ✕
              </Button>
            </div>
            <Button
              onClick={saveHighlight}
              size="sm"
              className="w-full bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600"
            >
              <Highlighter className="mr-2 w-4 h-4" /> Save Highlight
            </Button>
          </div>
        )}

        <Card className="mb-6 dark:bg-slate-800 dark:border-slate-700 overflow-hidden max-w-full">
          <CardContent className="p-4 sm:p-8 overflow-hidden max-w-full">
            <div 
              className="provision-content text-slate-700 dark:text-slate-300 text-base sm:text-lg leading-relaxed whitespace-pre-wrap select-text"
              style={{ 
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                wordBreak: 'normal',
                maxWidth: '100%',
                overflow: 'hidden',
                display: 'block',
                hyphens: 'none',
                WebkitHyphens: 'none'
              }}
              onMouseUp={handleTextSelection}
              onTouchEnd={handleTextSelection}
            >
              {renderHighlightedContent()}
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-wrap gap-4">
          <Button
            onClick={toggleBookmark}
            variant={isBookmarked ? 'default' : 'outline'}
            className={isBookmarked ? 'bg-emerald-600 hover:bg-emerald-700' : 'dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700'}
            data-testid="bookmark-btn"
          >
            {isBookmarked ? (
              <><BookmarkCheck className="mr-2 w-4 h-4" /> Bookmarked</>
            ) : (
              <><Bookmark className="mr-2 w-4 h-4" /> Bookmark</>
            )}
          </Button>

          <Button
            onClick={() => setNoteDialogOpen(true)}
            variant="outline"
            className="dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
            data-testid="add-note-btn"
          >
            <StickyNote className="mr-2 w-4 h-4" /> Add Note
          </Button>

          <Button
            onClick={shareAsText}
            variant="outline"
            className="dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
            data-testid="share-text-btn"
          >
            <Share2 className="mr-2 w-4 h-4" /> Copy Text
          </Button>

          <Button
            onClick={exportAsPDF}
            variant="outline"
            className="dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
            data-testid="export-pdf-btn"
          >
            <FileDown className="mr-2 w-4 h-4" /> Export PDF
          </Button>

          <Button
            onClick={exportAsImage}
            variant="outline"
            className="dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
            data-testid="export-image-btn"
          >
            <Image className="mr-2 w-4 h-4" /> Export Image
          </Button>
        </div>

        <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
          <DialogContent className="dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="dark:text-slate-100">Add Note</DialogTitle>
              <DialogDescription className="dark:text-slate-400">
                Add your personal notes about this provision
              </DialogDescription>
            </DialogHeader>
            <Textarea
              placeholder="Enter your note here..."
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              rows={6}
              className="dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100"
              data-testid="note-textarea"
            />
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setNoteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={saveNote}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="save-note-btn"
              >
                Save Note
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <BottomNav />
      </div>
    </>
  );
}
